import { forwardRef, type ButtonHTMLAttributes } from "react";
import clsx from "clsx";

export type SwitchProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "onChange"
> & {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  size?: "sm" | "md";
};

const TRACK_STYLES: Record<Required<SwitchProps>["size"], string> = {
  md: "h-6 w-12",
  sm: "h-5 w-10",
};

const THUMB_STYLES: Record<Required<SwitchProps>["size"], string> = {
  md: "h-5 w-5",
  sm: "h-4 w-4",
};

const THUMB_TRANSLATE: Record<
  Required<SwitchProps>["size"],
  { on: string; off: string }
> = {
  md: { on: "translate-x-6", off: "translate-x-1" },
  sm: { on: "translate-x-5", off: "translate-x-1" },
};

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
  (
    {
      checked = false,
      onCheckedChange,
      disabled,
      size = "md",
      className,
      ...props
    },
    ref,
  ) => {
    const track = TRACK_STYLES[size];
    const thumb = THUMB_STYLES[size];
    const translate = checked
      ? THUMB_TRANSLATE[size].on
      : THUMB_TRANSLATE[size].off;
    const checkedClass = checked
      ? "bg-primary"
      : "bg-surface-tertiary";

    return (
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-disabled={disabled}
        data-state={checked ? "on" : "off"}
        className={clsx(
          "relative inline-flex items-center rounded-pill border border-transparent transition-colors duration-fast ease-standard focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-primary",
          checkedClass,
          disabled && "cursor-not-allowed opacity-50",
          track,
          className,
        )}
        onClick={(event) => {
          if (disabled) {
            event.preventDefault();
            return;
          }
          props.onClick?.(event);
          if (event.defaultPrevented) return;
          onCheckedChange?.(!checked);
        }}
        {...props}
      >
        <span
          className={clsx(
            "pointer-events-none inline-block rounded-pill bg-surface-primary shadow-control transition-transform duration-fast ease-standard",
            thumb,
            translate,
          )}
        />
      </button>
    );
  },
);

Switch.displayName = "Switch";

export default Switch;
